'use client';

import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Lootbox, LootboxBonus } from '@/api/common';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import {
  EScratchTicketTheme,
  useLootboxBonus,
  useScratchGameLootbox,
} from '@/modules/promotions';
import { formatLootboxItemTitle } from '@/modules/promotions/helpers/fortuneWheel';
import { useGetCommonStore } from '@/store';
import scratchLine from '../../../../assets/scratch-line.png';
import Scratch from '../../../ScratchCard/game';
import { ScScratchTicket } from './styled';

interface IScratchTicket {
  isFinished: boolean;
  handleComplete: () => void;
  bonus: LootboxBonus | null;
  theme: EScratchTicketTheme;
  lootbox?: Lootbox;
}

export const ScratchTicket: FC<IScratchTicket> = ({
  handleComplete,
  isFinished,
  bonus,
  theme,
  lootbox,
}) => {
  const useCommonStore = useGetCommonStore();
  const { t } = useTranslation('scratchGame');

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { bonusAmount, title } = useLootboxBonus(bonus);

  const {
    isReachedDailyLimit,
    pendingLootboxes,
    isAuthenticated,
    currentConfig,
  } = useScratchGameLootbox();

  const isLockedField = useMemo(
    () => !lootbox || pendingLootboxes || !isAuthenticated,
    [lootbox, isAuthenticated, pendingLootboxes, isAuthenticated]
  );

  const isLockedOrLimit = isLockedField || isReachedDailyLimit;

  const isLocked = useMemo(
    () => isLockedField && !isReachedDailyLimit,
    [isLockedField, isReachedDailyLimit]
  );

  const isPremiumTheme = theme === EScratchTicketTheme.PREMIUM;

  const cardStatus = useMemo(() => {
    switch (true) {
      case !isAuthenticated:
        return 'unlogged';
      case isLocked:
        return 'logged';
      case isReachedDailyLimit && !bonus:
        return 'limit';
      case !!bonus:
        return 'win';
      default:
        return 'activated';
    }
  }, [
    isLockedField,
    isReachedDailyLimit,
    bonusAmount,
    isAuthenticated,
    isLocked,
    currentConfig,
    bonus,
  ]);

  const { fsPrizes, cashPrizes } = currentConfig[theme];

  return (
    <ScScratchTicket isFinished={isFinished} isPremium={isPremiumTheme}>
      <ImageComponent
        alt="ticket"
        className="ticket"
        fill
        src={
          isPremiumTheme
            ? '/scratchGame/scratch-ticket-vip.webp'
            : '/scratchGame/scratch-ticket.webp'
        }
      />

      <div className="top-text">
        <p className="title">
          {t(`boxOffice.card.topText.${cardStatus}.title`)}
        </p>
        <p className="subtitle">
          {t(`boxOffice.card.topText.${cardStatus}.description`, {
            minDep: currentConfig[theme].minDep,
          })}
        </p>
      </div>

      <div className="scratch-field-box">
        <div className={`scratch-field ${isLockedOrLimit ? 'locked' : ''}`}>
          {isLocked && !bonus && <IconComponent name="lockIcon" size="32px" />}
          {isReachedDailyLimit && !bonus && (
            <IconComponent name="clockIcon" size="23px" />
          )}
          <Scratch
            finishPercent={60}
            height={isMobileDevice ? 40 : 56}
            image={scratchLine}
            isMobileDevice={isMobileDevice}
            key={`${theme}-${isAuthenticated}`}
            onComplete={handleComplete}
            width={isMobileDevice ? 210 : 270}
          >
            <span className="prize">
              {isFinished && formatLootboxItemTitle(title)}
            </span>
          </Scratch>
        </div>
        <div className="possible-prizes">
          {isAuthenticated && isLockedOrLimit && (
            <>
              <span>
                {t('boxOffice.card.fs', {
                  fs1: fsPrizes.fs1,
                  fs2: fsPrizes.fs2,
                  fs3: fsPrizes.fs3,
                })}
              </span>
              <span>
                {t('boxOffice.card.cash', {
                  cash1: cashPrizes.cash1,
                  cash2: cashPrizes.cash2,
                  cash3: cashPrizes.cash3,
                })}
              </span>
            </>
          )}
        </div>
      </div>
    </ScScratchTicket>
  );
};
